import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { CompanyModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../utils/variables';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useOptionalUserState } from '../../state/user-state/user-state';

const PAGE_LIMIT = 100;

async function fetchCompanies(name: string): Promise<CompanyModel[]> {
  const service = SkyMapAxiosServiceFactory.instance.createCompanyServiceV1();
  const companies: CompanyModel[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const result = await service.getCompanies({ query: { name, limit: PAGE_LIMIT, offset } });
    companies.push(...result.data);
    const total = result.meta.pagination?.total ?? companies.length;
    hasMore = result.data.length > 0 && companies.length < total;
    offset += PAGE_LIMIT;
  }

  return companies;
}

export function useGetV1CompaniesQuery(name: string, options?: { enabled?: boolean }) {
  const { handleError, hasError, clearErrors, buildErrorList } = useErrorHandling();
  const userState = useOptionalUserState();
  const queryId = userState?.user?.id ?? '';
  const enabled = options?.enabled ?? true;

  const { data, refetch, error, isLoading } = useQuery({
    queryKey: ['getCompaniesV1', queryId, name],
    queryFn: () => fetchCompanies(name),
    enabled,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: refetch });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error, refetch]);

  return {
    companies: data,
    buildErrorList,
    hasError,
    isLoading,
  };
}
